<template>
  <!--
    AB#21837 — the WhatsApp tab of Order Communication (the placeholder from
    AB#21284, now live). Whatever was said about this order in the WhatsApp
    groups — a factory, a shipping agent, a supplier, sometimes a customer or
    an internal group — as a person would read it in the chat: them on the
    left (labelled with the group's name), us on the right, a rule per day.
    Above it the packing-list facts the feed extracted, because cartons against
    ordered quantity is the thing to check.

    Everything shown here is what ELSA will read once enabled (same reader,
    AB#21629): only messages with an evidenced link, never a person's name.
    Each message says how it was linked, so the desk can judge the linking.
  -->
  <div class="sc" data-test="whatsapp-thread">
    <div v-if="isLoading" class="sc-note" data-test="sc-loading">Loading WhatsApp messages…</div>

    <div v-else-if="error" class="sc-note sc-note--warn" data-test="sc-error">{{ error }}</div>

    <div v-else-if="data && !data.available" class="sc-empty" data-test="sc-unavailable">
      <b>The WhatsApp feed is not connected on this environment.</b>
      <span>Nothing is wrong with the order; WhatsApp messages just aren't available here.</span>
    </div>

    <div
      v-else-if="data && !data.messages.length && !data.packing.length"
      class="sc-empty"
      data-test="sc-empty"
    >
      <b>No WhatsApp messages linked to #{{ data.order_no || orderNo }}.</b>
      <span
        >Messages appear here when the feed can tie them to this order number with evidence: the
        number in the message, a packing list, or a reply to one of those.</span
      >
    </div>

    <template v-else-if="data">
      <section v-if="data.packing.length" class="sc-card" data-test="sc-packing">
        <h3 class="sc-card-title">Packing lists</h3>
        <div class="sc-pack">
          <div v-for="(p, i) in data.packing" :key="i" class="sc-pack-row">
            <div class="sc-pack-main">
              <b v-if="p.doc_no">Doc {{ p.doc_no }}</b>
              <b v-else-if="p.document">{{ p.document }}</b>
              <b v-else>Packing list</b>
              <span v-if="p.supplier_order_no">Supplier order {{ p.supplier_order_no }}</span>
              <span v-if="p.customer">{{ p.customer }}</span>
              <WhatsAppAttachment
                v-if="p.document && p.file_key"
                :order-item-id="orderItemId"
                :name="p.document"
                :file-key="p.file_key"
              />
            </div>
            <div class="sc-pack-facts">
              <span v-if="p.carton_qty !== undefined" class="sc-fact"
                ><b>{{ p.carton_qty }}</b> {{ p.carton_qty === 1 ? 'carton' : 'cartons' }}</span
              >
              <span v-for="s in p.styles ?? []" :key="s" class="sc-style">{{ s }}</span>
              <span v-if="p.sent_at" class="sc-when">{{ dayLabel(p.sent_at) }}</span>
            </div>
          </div>
        </div>
      </section>

      <section class="sc-card" data-test="sc-thread">
        <div class="sc-card-head">
          <h3 class="sc-card-title">
            Conversation
            <span v-if="chatName" class="sc-chat">· {{ chatName }}</span>
            <span v-else-if="chatCount > 1" class="sc-chat">· {{ chatCount }} groups</span>
          </h3>
          <span class="sc-count">{{ data.messages.length }} messages</span>
        </div>
        <label class="sc-toggle" data-test="sc-context-toggle">
          <input
            type="checkbox"
            :checked="withContext"
            :disabled="isLoading"
            @change="toggleContext"
          />
          Show surrounding messages
          <span class="sc-toggle-hint"
            >— the other messages in the same chat around these, which the feed did not confirm are
            about this order</span
          >
        </label>
        <p v-if="data.earlier_messages_omitted" class="sc-omitted" data-test="sc-omitted">
          Showing the most recent {{ data.messages.length }}; earlier messages are in WhatsApp.
        </p>
        <div class="sc-days">
          <div v-for="day in days" :key="day.label" class="sc-day">
            <div class="sc-day-rule">
              <span>{{ day.label }}</span>
            </div>
            <div
              v-for="(m, i) in day.messages"
              :key="m.id || `${m.at}-${i}`"
              class="sc-msg"
              :class="{ 'sc-msg--us': m.by === 'custimoo', 'sc-msg--ctx': m.context }"
              :data-test="m.context ? 'sc-msg-context' : `sc-msg-${m.by}`"
            >
              <div class="sc-bubble">
                <div class="sc-bubble-who">
                  <UserAvatar
                    v-if="m.sender_user || m.sender"
                    :name="m.sender"
                    :src="m.sender_user?.avatar_url"
                    :size="18"
                    tone="soft"
                  />
                  <span class="sc-who-name" data-test="sc-sender">{{ senderLabel(m) }}</span>
                  <span v-if="m.sender && chatCount > 1 && m.chat" class="sc-who-chat"
                    >in {{ m.chat }}</span
                  >
                  <span v-if="m.is_reply" class="sc-reply" title="A reply to an earlier message"
                    >↩ reply</span
                  >
                </div>
                <p v-if="m.text" class="sc-text">{{ renderMentions(m.text) }}</p>
                <WhatsAppAttachment
                  v-if="m.attachment"
                  :order-item-id="orderItemId"
                  :name="m.attachment"
                  :file-key="m.file_key"
                  :status="m.attachment_status"
                />
                <div class="sc-meta">
                  <span>{{ timeLabel(m.at) }}</span>
                  <span
                    v-if="m.context"
                    class="sc-linked"
                    title="The feed did not confirm this message is about this order"
                    >· same chat, not confirmed for this order</span
                  >
                  <span
                    v-else-if="m.linked_by"
                    class="sc-linked"
                    :title="`Linked to this order: ${linkedBy(m.linked_by)}`"
                    >· {{ linkedBy(m.linked_by) }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <p class="sc-foot">
        Messages come from the WhatsApp groups the feed follows; direct chats are never shown.
        Confirmed messages name the order or a packing list; ELSA, when enabled, reads only those,
        without names.
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import UserAvatar from '@/components/Shared/UserAvatar.vue';
import WhatsAppAttachment from './WhatsAppAttachment.vue';
import {
  LINKED_BY,
  dayLabel,
  renderMentions,
  senderLabel,
  timeLabel,
  useWhatsAppThread,
  type WhatsAppThread,
} from '@/composables/useWhatsAppThread';

const props = defineProps<{
  orderItemId: number;
  orderNo: string;
  /** AB#21841 — a thread the drawer already loaded (for the tab's count); else the panel loads its own. */
  thread?: WhatsAppThread | null;
}>();

const { isLoading, error, data, days, load, withContext, toggleContext } =
  props.thread ?? useWhatsAppThread(props.orderItemId);
const orderItemId = props.orderItemId;

const chatNames = computed(
  () => new Set((data.value?.messages ?? []).map((m) => m.chat).filter(Boolean))
);
const chatName = computed(() => (chatNames.value.size === 1 ? [...chatNames.value][0] : ''));
const chatCount = computed(() => chatNames.value.size);

const linkedBy = (method: string): string => LINKED_BY[method] ?? method;

onMounted(() => {
  if (!props.thread) void load();
});
</script>

<style scoped>
.sc {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px 0 8px;
  color: var(--ink);
}
.sc-note {
  font-size: 13px;
  color: var(--ink-soft);
}
.sc-note--warn {
  color: var(--danger);
}
.sc-empty {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 9px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: var(--ink-soft);
}
.sc-empty b {
  color: var(--ink);
  font-size: 14px;
}
.sc-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 9px;
  padding: 14px 16px;
}
.sc-card-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}
.sc-card-title {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 700;
}
.sc-chat {
  font-weight: 500;
  color: var(--ink-soft);
}
.sc-count {
  font-size: 12px;
  color: var(--ink-mute);
}
.sc-omitted {
  margin: 0 0 8px;
  font-size: 12px;
  color: var(--ink-mute);
}
.sc-pack {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sc-pack-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 12px;
  border: 1px solid var(--border-soft);
  border-radius: 8px;
  background: var(--surface-2);
  font-size: 13px;
}
.sc-pack-main {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.sc-pack-main span {
  color: var(--ink-soft);
  font-size: 12.5px;
}
.sc-pack-facts {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.sc-fact b {
  font-size: 15px;
}
.sc-style {
  font-size: 11.5px;
  padding: 2px 7px;
  border-radius: 999px;
  background: var(--surface-3);
  color: var(--ink-soft);
  font-variant-numeric: tabular-nums;
}
.sc-when {
  font-size: 12px;
  color: var(--ink-mute);
}
.sc-days {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.sc-day-rule {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0 6px;
  font-size: 11.5px;
  color: var(--ink-mute);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.sc-day-rule::before,
.sc-day-rule::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--border-soft);
}
.sc-msg {
  display: flex;
  margin: 3px 0;
}
.sc-msg--us {
  justify-content: flex-end;
}
.sc-bubble {
  max-width: min(78%, 560px);
  background: var(--surface-2);
  border: 1px solid var(--border-soft);
  border-radius: 12px 12px 12px 4px;
  padding: 8px 12px;
  font-size: 13.5px;
  line-height: 1.5;
}
.sc-msg--us .sc-bubble {
  background: var(--accent-soft);
  border-color: transparent;
  border-radius: 12px 12px 4px 12px;
}
.sc-bubble-who {
  font-size: 11.5px;
  font-weight: 600;
  color: var(--ink-soft);
  display: flex;
  gap: 8px;
  align-items: center;
}
.sc-reply {
  font-weight: 500;
  color: var(--ink-mute);
}
.sc-text {
  margin: 2px 0 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.sc-toggle {
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
  font-size: 12.5px;
  color: var(--ink-soft);
  margin: 0 0 6px;
  cursor: pointer;
}
.sc-toggle-hint {
  color: var(--ink-mute);
}
.sc-who-name {
  color: var(--ink);
}
.sc-who-chat {
  font-weight: 500;
  color: var(--ink-mute);
}
/* A context message: present but unconfirmed, so it steps back. */
.sc-msg--ctx .sc-bubble {
  opacity: 0.62;
  border-style: dashed;
}
.sc-meta {
  margin-top: 4px;
  font-size: 11px;
  color: var(--ink-mute);
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}
.sc-foot {
  margin: 0;
  font-size: 12px;
  color: var(--ink-mute);
}
@media (max-width: 480px) {
  .sc-bubble {
    max-width: 92%;
  }
}
</style>
